// features/sales/pages/SaleDetailPage.tsx - COMPLETE FIXED VERSION
/**
 * Sale Detail Page - Enterprise Edition
 * 
 * Features:
 * - Full sale information display
 * - Void/Refund with reason tracking
 * - Receipt reprint (HTML, PDF, Thermal)
 * - Audit trail timeline
 * - Price override indicators (when columns exist)
 * - Order modifiers display (when columns exist)
 * - Discount reason display
 * - Tax exempt indicator
 * - Payment status tracking
 * - Copy to clipboard support
 */

import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuthStore } from '@/store/authStore';
import { useToastStore } from '@/store/toastStore';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Card, CardContent } from '@/components/ui/Card';
import { Badge } from '@/components/ui/Badge';
import { Spinner } from '@/components/ui/Spinner';
import { ErrorState } from '@/components/ui/ErrorState';
import apiClient from '@/lib/api-client';
import {
    ArrowLeft, Receipt, XCircle, RotateCcw, Printer,
    User, Store, Calendar, CreditCard, Banknote,
    Smartphone, Building2, Tag, Package, DollarSign,
    AlertCircle, CheckCircle, X, Info, FileText,
    History, AlertTriangle, Copy, Users, Coffee,
    Download, ShieldCheck, MessageSquare,
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { clsx } from 'clsx';

// ============================================================
// TYPES
// ============================================================

interface SaleDetail {
    id: string;
    orderNumber: string;
    customerId: string | null;
    customerName?: string | null;
    cashierId: string;
    cashierName?: string;
    branchId: string;
    branchName?: string;
    shiftId: string | null;
    tableName?: string | null;
    status: 'completed' | 'voided' | 'refunded' | 'parked';
    saleType: 'retail' | 'wholesale' | 'clearance';
    subtotal: string;
    discountTotal: string;
    taxTotal: string;
    grandTotal: string;
    amountTendered: string | null;
    changeAmount: string | null;
    items: SaleItem[];
    payments: Payment[];
    notes: string | null;
    voidReason: string | null;
    voidedBy: string | null;
    voidedAt: string | null;
    discountReason: string | null;
    taxExempt?: boolean;
    priceOverrides?: any[];
    createdAt: string;
    updatedAt: string;
}

interface SaleItem {
    id: string;
    productId: string;
    productName?: string;
    variantName?: string | null;
    uomId: string;
    uomName?: string;
    uomAbbreviation?: string;
    quantity: string;
    unitPrice: string;
    discountAmount: string;
    taxAmount: string;
    totalAmount: string;
    costPrice: string;
    isReturned: boolean;
    returnedQuantity: string;
    notes: string | null;
    modifiers?: any[];
    isPriceOverridden?: boolean;
    originalPrice?: string | null;
}

interface Payment {
    id: string;
    paymentMethod: string;
    amount: string;
    reference: string | null;
    bankAccountId: string | null;
    bankAccountName?: string | null;
    mobileMoneyProvider: string | null;
    status: string;
    confirmedBy: string | null;
    confirmedAt: string | null;
    createdAt: string;
}

interface AuditEvent {
    type: 'created' | 'completed' | 'voided' | 'refunded' | 'printed';
    date: string;
    user: string | null;
    reason?: string | null;
    icon: React.ElementType;
    color: string;
}

// ============================================================
// CONSTANTS
// ============================================================

const STATUS_STYLES: Record<string, string> = {
    completed: 'bg-emerald-100 text-emerald-700 border-emerald-200',
    voided: 'bg-rose-100 text-rose-700 border-rose-200',
    refunded: 'bg-amber-100 text-amber-700 border-amber-200',
    parked: 'bg-slate-100 text-slate-600 border-slate-200',
};

const PAYMENT_ICONS: Record<string, React.ElementType> = {
    cash: Banknote,
    bank_transfer: Building2,
    pos_card: CreditCard,
    mobile_money: Smartphone,
};

const PAYMENT_STATUS_STYLES: Record<string, string> = {
    pending: 'bg-yellow-100 text-yellow-700',
    confirmed: 'bg-emerald-100 text-emerald-700',
    failed: 'bg-rose-100 text-rose-700',
    disputed: 'bg-orange-100 text-orange-700',
    reversed: 'bg-slate-100 text-slate-600',
};

const VOID_REASONS = [
    'Customer changed mind',
    'Pricing error',
    'Duplicate transaction',
    'Wrong product scanned',
    'Customer insufficient funds',
    'Technical error',
    'Other',
];

// ============================================================
// MAIN COMPONENT
// ============================================================

export function SaleDetailPage() {
    const { saleId } = useParams<{ saleId: string }>();
    const navigate = useNavigate();
    const user = useAuthStore((s) => s.user);
    const tenantId = user?.tenantId || '';
    const queryClient = useQueryClient();
    const addToast = useToastStore((s) => s.addToast);

    // Modal states
    const [showVoid, setShowVoid] = useState(false);
    const [showRefund, setShowRefund] = useState(false);

    // Form states
    const [voidReason, setVoidReason] = useState('');
    const [voidCustomReason, setVoidCustomReason] = useState('');
    const [refundItems, setRefundItems] = useState<{ saleItemId: string; quantity: string }[]>([]);
    const [refundReason, setRefundReason] = useState('');
    const [refundMethod, setRefundMethod] = useState('cash');

    // Queries
    const { data: sale, isLoading, isError, refetch } = useQuery<SaleDetail>({
        queryKey: ['sale', saleId],
        queryFn: async () => {
            const response = await apiClient.get(`/tenants/${tenantId}/sales/${saleId}`);
            return response.data?.data || response.data;
        },
        enabled: !!tenantId && !!saleId,
    });

    // Mutations
    const voidMutation = useMutation({
        mutationFn: () => {
            const reason = voidReason === 'Other' ? voidCustomReason : voidReason;
            if (!reason) throw new Error('Please provide a void reason.');
            return apiClient.post(`/tenants/${tenantId}/sales/${saleId}/void`, { reason });
        },
        onSuccess: () => {
            addToast({ type: 'success', title: 'Sale voided', message: 'Inventory has been restored.' });
            setShowVoid(false);
            queryClient.invalidateQueries({ queryKey: ['sale', saleId] });
            queryClient.invalidateQueries({ queryKey: ['sales'] });
            refetch();
        },
        onError: (err: any) => {
            addToast({ type: 'error', title: 'Void failed', message: err?.response?.data?.error?.message || err.message });
        },
    });

    const refundMutation = useMutation({
        mutationFn: () => {
            const items = refundItems.filter(r => parseFloat(r.quantity) > 0);
            if (items.length === 0) throw new Error('Select at least one item to refund.');
            if (!refundReason) throw new Error('Please provide a refund reason.');
            return apiClient.post(`/tenants/${tenantId}/sales/${saleId}/refund`, {
                items,
                reason: refundReason,
                refundPaymentMethod: refundMethod,
            });
        },
        onSuccess: () => {
            addToast({ type: 'success', title: 'Refund processed', message: 'Inventory has been updated.' });
            setShowRefund(false);
            queryClient.invalidateQueries({ queryKey: ['sale', saleId] });
            queryClient.invalidateQueries({ queryKey: ['sales'] });
            refetch();
        },
        onError: (err: any) => {
            addToast({ type: 'error', title: 'Refund failed', message: err?.response?.data?.error?.message || err.message });
        },
    });

    // Helpers
    const formatNaira = (val: string | number): string => {
        const num = typeof val === 'string' ? parseFloat(val) : val;
        return isNaN(num) ? '₦0.00' : `₦${num.toLocaleString(undefined, { minimumFractionDigits: 2 })}`;
    };

    const formatDateTime = (d: string | null | undefined): string => {
        if (!d) return '—';
        return new Date(d).toLocaleString('en-NG', {
            day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
        });
    };

    const formatDate = (d: string | null | undefined): string => {
        if (!d) return '—';
        return new Date(d).toLocaleDateString('en-NG', { day: 'numeric', month: 'short', year: 'numeric' });
    };

    const getRefundableQty = (item: SaleItem): number => {
        return parseFloat(item.quantity) - parseFloat(item.returnedQuantity || '0');
    };

    const copyToClipboard = async (text: string) => {
        try {
            await navigator.clipboard.writeText(text);
            addToast({ type: 'success', title: 'Copied', message: 'Text copied to clipboard' });
        } catch {
            const textarea = document.createElement('textarea');
            textarea.value = text;
            document.body.appendChild(textarea);
            textarea.select();
            document.execCommand('copy');
            document.body.removeChild(textarea);
            addToast({ type: 'success', title: 'Copied', message: 'Text copied to clipboard' });
        }
    };

    const handlePrintReceipt = (format: 'html' | 'pdf' | 'thermal') => {
        const url = `/api/tenants/${tenantId}/sales/${saleId}/receipt/${format}`;
        if (format === 'html') {
            window.open(url, '_blank');
        } else {
            const a = document.createElement('a');
            a.href = url;
            a.download = `receipt-${sale?.orderNumber || saleId}.${format === 'thermal' ? 'txt' : format}`;
            a.click();
        }
        addToast({ type: 'info', title: 'Receipt', message: `${format.toUpperCase()} receipt generated` });
    };

    // Helper function for creating audit events with proper typing
    const createAuditEvent = (
        type: AuditEvent['type'],
        date: string,
        user: string | null,
        icon: React.ElementType,
        color: string,
        reason?: string | null
    ): AuditEvent => ({
        type,
        date,
        user,
        icon,
        color,
        reason: reason || undefined,
    });

    // Compute audit trail
    const auditTrail: AuditEvent[] = sale ? [
        createAuditEvent('created', sale.createdAt, sale.cashierName || null, Receipt, 'text-blue-500'),
        ...(sale.status !== 'parked' ? [
            createAuditEvent('completed', sale.updatedAt, sale.cashierName || null, CheckCircle, 'text-emerald-500')
        ] : []),
        ...(sale.voidedAt ? [
            createAuditEvent('voided', sale.voidedAt, sale.voidedBy || null, XCircle, 'text-rose-500', sale.voidReason)
        ] : []),
        ...(sale.status === 'refunded' ? [
            createAuditEvent('refunded', sale.updatedAt, null, RotateCcw, 'text-amber-500', 'Items refunded')
        ] : []),
    ].sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime()) : [];

    const canVoid = sale?.status === 'completed';
    const canRefund = sale?.status === 'completed';

    // Loading state
    if (isLoading) {
        return (
            <div className="flex items-center justify-center py-32">
                <div className="text-center space-y-4">
                    <Spinner size="xl" />
                    <p className="text-slate-500 text-sm">Loading sale details...</p>
                </div>
            </div>
        );
    }

    // Error state
    if (isError || !sale) {
        return (
            <ErrorState
                title="Sale Not Found"
                message="The sale you're looking for doesn't exist or has been deleted."
                onRetry={() => refetch()}
                onBack={() => navigate('/sales')}
                variant="error"
            />
        );
    }

    return (
        <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-emerald-50/30">
            <div className="max-w-6xl mx-auto p-4 lg:p-6 space-y-5">
                {/* ========== HEADER ========== */}
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                    <div className="flex items-center gap-3">
                        <button
                            onClick={() => navigate('/sales')}
                            className="p-2 rounded-xl bg-white border border-slate-200 hover:bg-slate-50 transition-colors"
                            type="button"
                        >
                            <ArrowLeft size={18} className="text-slate-600" />
                        </button>
                        <div className="p-2.5 rounded-2xl bg-gradient-to-br from-teal-500 to-teal-700 shadow-lg shadow-teal-500/20">
                            <Receipt size={20} className="text-white" />
                        </div>
                        <div>
                            <div className="flex items-center gap-2 flex-wrap">
                                <h1 className="text-xl font-bold text-slate-900 font-mono">
                                    {sale.orderNumber}
                                </h1>
                                <span className={clsx(
                                    'px-2.5 py-0.5 text-xs font-medium rounded-full border',
                                    STATUS_STYLES[sale.status]
                                )}>
                                    {sale.status}
                                </span>
                                {sale.taxExempt && (
                                    <span title="Tax Exempt">
                                        <Badge variant="info" size="sm">
                                            <ShieldCheck size={10} className="mr-1" />
                                            Tax Exempt
                                        </Badge>
                                    </span>
                                )}
                            </div>
                            <p className="text-xs text-slate-500 mt-0.5">
                                {formatDateTime(sale.createdAt)}
                            </p>
                        </div>
                    </div>

                    {/* Action Buttons */}
                    <div className="flex items-center gap-2 flex-wrap">
                        <Button
                            variant="secondary"
                            size="sm"
                            onClick={() => handlePrintReceipt('html')}
                        >
                            <Printer size={14} className="mr-1" /> Print
                        </Button>

                        <Button
                            variant="secondary"
                            size="sm"
                            onClick={() => handlePrintReceipt('pdf')}
                        >
                            <Download size={14} className="mr-1" /> PDF
                        </Button>

                        <button
                            type="button"
                            onClick={() => addToast({
                                type: 'info',
                                title: 'WhatsApp Receipts (Coming Soon)',
                                message: 'WhatsApp Receipt delivery is currently in development and will be available in the upcoming release.',
                            })}
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-lg bg-emerald-50 text-emerald-800 border border-emerald-200 hover:bg-emerald-100 transition-colors shadow-xs"
                        >
                            <MessageSquare size={13} className="text-emerald-600" />
                            <span>WhatsApp</span>
                            <span className="text-[9px] uppercase px-1.5 py-0.5 bg-emerald-600 text-white font-extrabold rounded-full">
                                Coming Soon
                            </span>
                        </button>

                        {canVoid && (
                            <Button
                                variant="danger"
                                size="sm"
                                onClick={() => { setVoidReason(''); setVoidCustomReason(''); setShowVoid(true); }}
                            >
                                <XCircle size={14} className="mr-1" /> Void
                            </Button>
                        )}
                        {canRefund && (
                            <Button
                                variant="warning"
                                size="sm"
                                onClick={() => {
                                    setRefundItems(sale.items.map((i) => ({ saleItemId: i.id, quantity: '0' })));
                                    setRefundReason('');
                                    setShowRefund(true);
                                }}
                            >
                                <RotateCcw size={14} className="mr-1" /> Refund
                            </Button>
                        )}
                    </div>
                </div>

                {/* ========== QUICK STATS ========== */}
                <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                    <InfoCard label="Sale Type" value={sale.saleType} icon={Tag} />
                    <InfoCard label="Branch" value={sale.branchName || '—'} icon={Store} />
                    <InfoCard label="Cashier" value={sale.cashierName || '—'} icon={User} />
                    <InfoCard label="Customer" value={sale.customerName || 'Walk-in'} icon={Users} />
                    {sale.tableName && (
                        <InfoCard label="Table" value={sale.tableName} icon={Coffee} />
                    )}
                </div>

                {/* ========== MAIN CONTENT GRID ========== */}
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
                    {/* Left Column - Transaction & Payment */}
                    <div className="lg:col-span-1 space-y-5">
                        {/* Transaction Details */}
                        <Card>
                            <CardContent className="p-5">
                                <h3 className="text-sm font-semibold text-slate-800 mb-4 flex items-center gap-2">
                                    <Info size={16} className="text-teal-600" />
                                    Transaction Details
                                </h3>
                                <div className="space-y-2">
                                    <DetailRow
                                        label="Order Number"
                                        value={sale.orderNumber}
                                        mono
                                        copyable
                                        onCopy={() => copyToClipboard(sale.orderNumber)}
                                    />
                                    <DetailRow label="Date" value={formatDateTime(sale.createdAt)} />
                                    <DetailRow label="Cashier" value={sale.cashierName || '—'} />
                                    <DetailRow label="Branch" value={sale.branchName || '—'} />
                                    <DetailRow
                                        label="Sale Type"
                                        value={
                                            <Badge variant="neutral" size="sm">
                                                {sale.saleType}
                                            </Badge>
                                        }
                                    />
                                    {sale.tableName && <DetailRow label="Table" value={sale.tableName} />}
                                    {sale.customerName && <DetailRow label="Customer" value={sale.customerName} />}
                                    {sale.discountReason && (
                                        <DetailRow
                                            label="Discount Reason"
                                            value={sale.discountReason}
                                            color="text-amber-600"
                                        />
                                    )}
                                    {sale.notes && (
                                        <DetailRow
                                            label="Notes"
                                            value={sale.notes}
                                            color="text-slate-500"
                                        />
                                    )}
                                    {sale.voidReason && (
                                        <DetailRow
                                            label="Void Reason"
                                            value={sale.voidReason}
                                            color="text-rose-600"
                                        />
                                    )}
                                </div>
                            </CardContent>
                        </Card>

                        {/* Payment Summary */}
                        <Card>
                            <CardContent className="p-5">
                                <h3 className="text-sm font-semibold text-slate-800 mb-4 flex items-center gap-2">
                                    <DollarSign size={16} className="text-teal-600" />
                                    Payment Summary
                                </h3>

                                <div className="space-y-2 mb-4">
                                    <DetailRow label="Subtotal" value={formatNaira(sale.subtotal)} />
                                    {parseFloat(sale.discountTotal) > 0 && (
                                        <DetailRow
                                            label="Discount"
                                            value={`-${formatNaira(sale.discountTotal)}`}
                                            color="text-amber-600"
                                        />
                                    )}
                                    {!sale.taxExempt && (
                                        <DetailRow label="Tax" value={formatNaira(sale.taxTotal)} />
                                    )}
                                    {sale.taxExempt && (
                                        <DetailRow
                                            label="Tax"
                                            value="Exempt"
                                            color="text-blue-600"
                                        />
                                    )}
                                    <div className="flex justify-between items-center py-3 border-t-2 border-slate-200">
                                        <span className="font-bold text-slate-800">Grand Total</span>
                                        <span className="text-xl font-bold text-teal-600">
                                            {formatNaira(sale.grandTotal)}
                                        </span>
                                    </div>
                                    {sale.amountTendered && (
                                        <>
                                            <DetailRow label="Amount Tendered" value={formatNaira(sale.amountTendered)} />
                                            {parseFloat(sale.changeAmount || '0') > 0 && (
                                                <DetailRow
                                                    label="Change"
                                                    value={formatNaira(sale.changeAmount || '0')}
                                                    color="text-emerald-600"
                                                />
                                            )}
                                        </>
                                    )}
                                </div>

                                {/* Payments List */}
                                {sale.payments && sale.payments.length > 0 && (
                                    <div className="pt-3 border-t border-slate-100">
                                        <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-3">
                                            Payments ({sale.payments.length})
                                        </p>
                                        <div className="space-y-2">
                                            {sale.payments.map((p) => {
                                                const Icon = PAYMENT_ICONS[p.paymentMethod] || Banknote;
                                                return (
                                                    <div
                                                        key={p.id}
                                                        className="p-3 bg-slate-50 rounded-xl space-y-2"
                                                    >
                                                        <div className="flex items-center justify-between">
                                                            <div className="flex items-center gap-2">
                                                                <span
                                                                    className="p-1.5 rounded-lg bg-white shadow-sm"
                                                                    title={p.paymentMethod?.replace(/_/g, ' ')}
                                                                >
                                                                    <Icon size={14} className="text-slate-600" />
                                                                </span>
                                                                <div>
                                                                    <span className="text-sm font-medium capitalize">
                                                                        {p.paymentMethod?.replace(/_/g, ' ')}
                                                                    </span>
                                                                    {p.mobileMoneyProvider && (
                                                                        <span className="text-xs text-slate-400 ml-1">
                                                                            ({p.mobileMoneyProvider})
                                                                        </span>
                                                                    )}
                                                                </div>
                                                            </div>
                                                            <span className="text-sm font-bold text-slate-800">
                                                                {formatNaira(p.amount)}
                                                            </span>
                                                        </div>

                                                        <div className="flex items-center justify-between text-xs">
                                                            <div className="flex items-center gap-2">
                                                                <span className={clsx(
                                                                    'px-2 py-0.5 rounded-full font-medium',
                                                                    PAYMENT_STATUS_STYLES[p.status] || ''
                                                                )}>
                                                                    {p.status}
                                                                </span>
                                                                {p.reference && (
                                                                    <code className="text-slate-400 bg-slate-100 px-1.5 py-0.5 rounded">
                                                                        {p.reference}
                                                                    </code>
                                                                )}
                                                            </div>
                                                            <span className="text-slate-400">
                                                                {formatDate(p.createdAt)}
                                                            </span>
                                                        </div>

                                                        {p.bankAccountName && (
                                                            <p className="text-xs text-slate-400 flex items-center gap-1">
                                                                <Building2 size={10} />
                                                                {p.bankAccountName}
                                                            </p>
                                                        )}
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </div>
                                )}
                            </CardContent>
                        </Card>
                    </div>

                    {/* Right Column - Items & Audit */}
                    <div className="lg:col-span-2 space-y-5">
                        {/* Line Items */}
                        <Card>
                            <CardContent className="p-5">
                                <h3 className="text-sm font-semibold text-slate-800 mb-4 flex items-center gap-2">
                                    <Package size={16} className="text-teal-600" />
                                    Items ({sale.items?.length || 0})
                                </h3>

                                <div className="overflow-x-auto">
                                    <table className="min-w-full text-sm">
                                        <thead>
                                            <tr className="border-b-2 border-slate-200">
                                                <th className="text-left py-3 text-[11px] font-semibold text-slate-500 uppercase tracking-wider">
                                                    Product
                                                </th>
                                                <th className="text-right py-3 text-[11px] font-semibold text-slate-500 uppercase tracking-wider">
                                                    Qty
                                                </th>
                                                <th className="text-right py-3 text-[11px] font-semibold text-slate-500 uppercase tracking-wider">
                                                    Price
                                                </th>
                                                <th className="text-right py-3 text-[11px] font-semibold text-slate-500 uppercase tracking-wider">
                                                    Discount
                                                </th>
                                                <th className="text-right py-3 text-[11px] font-semibold text-slate-500 uppercase tracking-wider">
                                                    Total
                                                </th>
                                            </tr>
                                        </thead>
                                        <tbody className="divide-y divide-slate-100">
                                            {sale.items?.map((item) => {
                                                return (
                                                    <tr
                                                        key={item.id}
                                                        className={clsx(
                                                            'group transition-colors',
                                                            item.isReturned && 'bg-amber-50/30',
                                                            item.isPriceOverridden && 'bg-orange-50/20'
                                                        )}
                                                    >
                                                        <td className="py-3">
                                                            <div className="flex items-center gap-2">
                                                                {item.isPriceOverridden && (
                                                                    <span title="Price was overridden">
                                                                        <AlertTriangle
                                                                            size={12}
                                                                            className="text-orange-500 flex-shrink-0"
                                                                        />
                                                                    </span>
                                                                )}
                                                                <div>
                                                                    <p className="font-medium text-slate-800">
                                                                        {item.productName || 'Unknown Product'}
                                                                    </p>
                                                                    {item.variantName && (
                                                                        <p className="text-xs text-slate-400">
                                                                            {item.variantName}
                                                                        </p>
                                                                    )}
                                                                    <p className="text-xs text-slate-400">
                                                                        {item.uomName || item.uomAbbreviation || 'unit'}
                                                                        {item.notes && ` · ${item.notes}`}
                                                                    </p>
                                                                    {/* Modifiers display (when available) */}
                                                                    {item.modifiers && item.modifiers.length > 0 && (
                                                                        <div className="flex flex-wrap gap-1 mt-1">
                                                                            {item.modifiers.map((mod: any, i: number) => (
                                                                                <span
                                                                                    key={i}
                                                                                    className="text-[10px] bg-purple-100 text-purple-700 px-1.5 py-0.5 rounded-full"
                                                                                >
                                                                                    {mod.name}: {mod.option}
                                                                                </span>
                                                                            ))}
                                                                        </div>
                                                                    )}
                                                                </div>
                                                            </div>
                                                        </td>
                                                        <td className="py-3 text-right">
                                                            <span className="font-medium">
                                                                {parseFloat(item.quantity).toLocaleString()}
                                                            </span>
                                                            {parseFloat(item.returnedQuantity) > 0 && (
                                                                <p className="text-xs text-amber-500">
                                                                    -{parseFloat(item.returnedQuantity).toLocaleString()}
                                                                </p>
                                                            )}
                                                        </td>
                                                        <td className="py-3 text-right text-slate-600">
                                                            {formatNaira(item.unitPrice)}
                                                            {item.isPriceOverridden && item.originalPrice && (
                                                                <p className="text-[10px] text-orange-400 line-through">
                                                                    {formatNaira(item.originalPrice)}
                                                                </p>
                                                            )}
                                                        </td>
                                                        <td className="py-3 text-right">
                                                            {parseFloat(item.discountAmount) > 0 ? (
                                                                <span className="text-amber-600">
                                                                    -{formatNaira(item.discountAmount)}
                                                                </span>
                                                            ) : (
                                                                <span className="text-slate-300">—</span>
                                                            )}
                                                        </td>
                                                        <td className="py-3 text-right font-semibold text-slate-800">
                                                            {formatNaira(item.totalAmount)}
                                                        </td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </table>
                                </div>
                            </CardContent>
                        </Card>

                        {/* Audit Trail */}
                        {auditTrail.length > 0 && (
                            <Card>
                                <CardContent className="p-5">
                                    <h3 className="text-sm font-semibold text-slate-800 mb-4 flex items-center gap-2">
                                        <History size={16} className="text-teal-600" />
                                        Audit Trail
                                    </h3>

                                    <div className="relative">
                                        {/* Timeline line */}
                                        <div className="absolute left-4 top-2 bottom-2 w-px bg-slate-200" />

                                        <div className="space-y-4">
                                            {auditTrail.map((event, i) => {
                                                const Icon = event.icon;
                                                return (
                                                    <div key={i} className="flex items-start gap-4 relative">
                                                        <div className={clsx(
                                                            'relative z-10 p-2 rounded-full bg-white border-2 flex-shrink-0',
                                                            event.color.replace('text-', 'border-')
                                                        )}>
                                                            <Icon size={14} className={event.color} />
                                                        </div>
                                                        <div className="flex-1 min-w-0 pt-0.5">
                                                            <div className="flex items-center gap-2 flex-wrap">
                                                                <p className="text-sm font-medium text-slate-800 capitalize">
                                                                    {event.type}
                                                                </p>
                                                                <span className="text-xs text-slate-400">
                                                                    {formatDateTime(event.date)}
                                                                </span>
                                                            </div>
                                                            {event.user && (
                                                                <p className="text-xs text-slate-500">By: {event.user}</p>
                                                            )}
                                                            {event.reason && (
                                                                <div className="mt-1 p-2 bg-slate-50 rounded-lg">
                                                                    <p className="text-xs text-slate-600 italic">
                                                                        &ldquo;{event.reason}&rdquo;
                                                                    </p>
                                                                </div>
                                                            )}
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </div>
                                </CardContent>
                            </Card>
                        )}
                    </div>
                </div>
            </div>

            {/* ========== VOID MODAL ========== */}
            <AnimatePresence>
                {showVoid && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4"
                        onClick={(e) => { if (e.target === e.currentTarget) setShowVoid(false); }}
                    >
                        <motion.div
                            initial={{ scale: 0.95, opacity: 0, y: 20 }}
                            animate={{ scale: 1, opacity: 1, y: 0 }}
                            exit={{ scale: 0.95, opacity: 0, y: 20 }}
                            className="bg-white rounded-3xl shadow-2xl w-full max-w-md overflow-hidden"
                        >
                            {/* Modal Header */}
                            <div className="bg-gradient-to-r from-rose-600 to-rose-700 px-6 py-4 text-white">
                                <div className="flex items-center justify-between">
                                    <div className="flex items-center gap-3">
                                        <div className="p-1.5 rounded-lg bg-white/20">
                                            <XCircle size={18} className="text-white" />
                                        </div>
                                        <div>
                                            <h2 className="text-lg font-bold">Void Sale</h2>
                                            <p className="text-rose-100 text-xs">
                                                This action cannot be undone
                                            </p>
                                        </div>
                                    </div>
                                    <button
                                        onClick={() => setShowVoid(false)}
                                        className="p-1.5 rounded-lg bg-white/20 hover:bg-white/30 transition-colors"
                                        type="button"
                                    >
                                        <X size={16} className="text-white" />
                                    </button>
                                </div>
                            </div>

                            {/* Modal Body */}
                            <div className="p-6 space-y-4">
                                <div className="p-3 bg-rose-50 rounded-xl text-sm text-rose-700 flex items-start gap-2">
                                    <AlertCircle size={16} className="flex-shrink-0 mt-0.5" />
                                    <div>
                                        <p className="font-medium">What happens when you void?</p>
                                        <ul className="text-xs mt-1 space-y-0.5 list-disc list-inside">
                                            <li>All inventory will be restored</li>
                                            <li>Shift totals will be reversed</li>
                                            <li>Bank balances will be adjusted</li>
                                            <li>This action is permanently recorded</li>
                                        </ul>
                                    </div>
                                </div>

                                <div>
                                    <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">
                                        Reason *
                                    </label>
                                    <div className="grid grid-cols-2 gap-2 mb-3">
                                        {VOID_REASONS.map((r) => (
                                            <button
                                                key={r}
                                                type="button"
                                                onClick={() => setVoidReason(r)}
                                                className={clsx(
                                                    'p-2.5 rounded-xl border-2 text-xs font-medium text-left transition-all',
                                                    voidReason === r
                                                        ? 'border-rose-400 bg-rose-50 text-rose-700'
                                                        : 'border-slate-200 text-slate-500 hover:border-slate-300'
                                                )}
                                            >
                                                {r}
                                            </button>
                                        ))}
                                    </div>
                                    {voidReason === 'Other' && (
                                        <textarea
                                            value={voidCustomReason}
                                            onChange={(e) => setVoidCustomReason(e.target.value)}
                                            rows={2}
                                            className="w-full px-3 py-2 border-2 border-slate-200 rounded-xl text-sm resize-none focus:ring-2 focus:ring-rose-500/20 focus:border-rose-400"
                                            placeholder="Explain why this sale is being voided..."
                                            required
                                        />
                                    )}
                                </div>
                            </div>

                            {/* Modal Footer */}
                            <div className="px-6 py-4 bg-slate-50 border-t border-slate-200 flex justify-end gap-3">
                                <Button variant="secondary" onClick={() => setShowVoid(false)}>
                                    Cancel
                                </Button>
                                <Button
                                    variant="danger"
                                    onClick={() => voidMutation.mutate()}
                                    isLoading={voidMutation.isPending}
                                    disabled={!voidReason || (voidReason === 'Other' && !voidCustomReason)}
                                >
                                    <XCircle size={16} className="mr-1.5" /> Confirm Void
                                </Button>
                            </div>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>

            {/* ========== REFUND MODAL ========== */}
            <AnimatePresence>
                {showRefund && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4"
                        onClick={(e) => { if (e.target === e.currentTarget) setShowRefund(false); }}
                    >
                        <motion.div
                            initial={{ scale: 0.95, opacity: 0, y: 20 }}
                            animate={{ scale: 1, opacity: 1, y: 0 }}
                            exit={{ scale: 0.95, opacity: 0, y: 20 }}
                            className="bg-white rounded-3xl shadow-2xl w-full max-w-lg max-h-[85vh] overflow-hidden flex flex-col"
                        >
                            {/* Modal Header */}
                            <div className="bg-gradient-to-r from-amber-500 to-amber-600 px-6 py-4 text-white flex items-center justify-between flex-shrink-0">
                                <div className="flex items-center gap-3">
                                    <div className="p-1.5 rounded-lg bg-white/20">
                                        <RotateCcw size={18} className="text-white" />
                                    </div>
                                    <div>
                                        <h2 className="text-lg font-bold">Refund Items</h2>
                                        <p className="text-amber-100 text-xs">
                                            Select items and quantities to refund
                                        </p>
                                    </div>
                                </div>
                                <button
                                    onClick={() => setShowRefund(false)}
                                    className="p-1.5 rounded-lg bg-white/20 hover:bg-white/30 transition-colors"
                                    type="button"
                                >
                                    <X size={16} className="text-white" />
                                </button>
                            </div>

                            {/* Modal Body */}
                            <div className="flex-1 overflow-y-auto p-6 space-y-4">
                                {sale.items?.map((item) => {
                                    const refundable = getRefundableQty(item);
                                    const refundItem = refundItems.find(r => r.saleItemId === item.id);
                                    const refundQty = parseFloat(refundItem?.quantity || '0');
                                    const refundAmount = refundQty * parseFloat(item.unitPrice);

                                    return (
                                        <div
                                            key={item.id}
                                            className={clsx(
                                                'p-4 rounded-xl border-2 transition-all',
                                                item.isReturned
                                                    ? 'border-slate-200 bg-slate-50/50 opacity-60'
                                                    : refundQty > 0
                                                        ? 'border-amber-300 bg-amber-50/30'
                                                        : 'border-slate-200'
                                            )}
                                        >
                                            <div className="flex items-center justify-between mb-2">
                                                <div>
                                                    <p className="text-sm font-semibold text-slate-800">
                                                        {item.productName}
                                                    </p>
                                                    <p className="text-xs text-slate-400">
                                                        Sold: {parseFloat(item.quantity).toLocaleString()} × {formatNaira(item.unitPrice)}
                                                        {parseFloat(item.returnedQuantity) > 0 && (
                                                            <span className="text-amber-500">
                                                                {' '}· Returned: {parseFloat(item.returnedQuantity).toLocaleString()}
                                                            </span>
                                                        )}
                                                    </p>
                                                </div>
                                                {item.isReturned ? (
                                                    <Badge variant="danger" size="sm">
                                                        Fully Returned
                                                    </Badge>
                                                ) : (
                                                    <span className="text-xs text-slate-400">
                                                        Max: {refundable.toLocaleString()}
                                                    </span>
                                                )}
                                            </div>

                                            {!item.isReturned && (
                                                <div className="flex items-center gap-3">
                                                    <Input
                                                        type="number"
                                                        step="0.001"
                                                        min="0"
                                                        max={refundable}
                                                        value={refundItem?.quantity || '0'}
                                                        onChange={(e) => {
                                                            setRefundItems(refundItems.map(r =>
                                                                r.saleItemId === item.id
                                                                    ? { ...r, quantity: e.target.value }
                                                                    : r
                                                            ));
                                                        }}
                                                        placeholder="0"
                                                        className="w-28"
                                                    />
                                                    {refundQty > 0 && (
                                                        <span className="text-sm text-amber-600 font-medium">
                                                            {formatNaira(refundAmount)}
                                                        </span>
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                    );
                                })}

                                {refundItems.some(r => parseFloat(r.quantity) > 0) && (
                                    <div className="p-3 bg-amber-50 rounded-xl">
                                        <p className="text-sm font-semibold text-amber-700">
                                            Total Refund:{' '}
                                            {formatNaira(
                                                refundItems.reduce((sum, r) => {
                                                    const item = sale.items.find(i => i.id === r.saleItemId);
                                                    return sum + (parseFloat(r.quantity) * parseFloat(item?.unitPrice || '0'));
                                                }, 0)
                                            )}
                                        </p>
                                    </div>
                                )}

                                <div>
                                    <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">
                                        Refund Method
                                    </label>
                                    <select
                                        value={refundMethod}
                                        onChange={(e) => setRefundMethod(e.target.value)}
                                        className="w-full px-3 py-2.5 border-2 border-slate-200 rounded-xl text-sm bg-white"
                                    >
                                        <option value="cash">Cash</option>
                                        <option value="bank_transfer">Bank Transfer</option>
                                        <option value="store_credit">Store Credit</option>
                                    </select>
                                </div>

                                <div>
                                    <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">
                                        Reason *
                                    </label>
                                    <textarea
                                        value={refundReason}
                                        onChange={(e) => setRefundReason(e.target.value)}
                                        rows={2}
                                        className="w-full px-3 py-2.5 border-2 border-slate-200 rounded-xl text-sm resize-none focus:ring-2 focus:ring-teal-500/20 focus:border-teal-400"
                                        placeholder="Why is this being refunded?"
                                        required
                                    />
                                </div>
                            </div>

                            {/* Modal Footer */}
                            <div className="px-6 py-4 bg-slate-50 border-t border-slate-200 flex justify-end gap-3 flex-shrink-0">
                                <Button variant="secondary" onClick={() => setShowRefund(false)}>
                                    Cancel
                                </Button>
                                <Button
                                    variant="warning"
                                    onClick={() => refundMutation.mutate()}
                                    isLoading={refundMutation.isPending}
                                    disabled={!refundReason || refundItems.every(r => parseFloat(r.quantity) <= 0)}
                                >
                                    <RotateCcw size={16} className="mr-1.5" /> Process Refund
                                </Button>
                            </div>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
}

// ============================================================
// SUB-COMPONENTS
// ============================================================

const InfoCard: React.FC<{ label: string; value: string; icon: React.ElementType }> = ({
    label,
    value,
    icon: Icon,
}) => (
    <div className="bg-white rounded-xl border border-slate-200 p-4 hover:shadow-md transition-shadow">
        <div className="flex items-center gap-2.5">
            <div className="p-2 rounded-lg bg-gradient-to-br from-teal-50 to-teal-100">
                <Icon size={14} className="text-teal-600" />
            </div>
            <div className="min-w-0">
                <p className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider truncate">
                    {label}
                </p>
                <p className="text-sm font-medium text-slate-800 capitalize truncate">{value}</p>
            </div>
        </div>
    </div>
);

const DetailRow: React.FC<{
    label: string;
    value: string | React.ReactNode;
    mono?: boolean;
    color?: string;
    copyable?: boolean;
    onCopy?: () => void;
}> = ({ label, value, mono, color, copyable, onCopy }) => (
    <div className="flex items-center justify-between py-2 px-2 rounded-lg hover:bg-slate-50 transition-colors group">
        <span className="text-xs text-slate-500 flex-shrink-0">{label}</span>
        <div className="flex items-center gap-1.5 ml-2">
            {typeof value === 'string' ? (
                <span
                    className={clsx(
                        'text-sm font-medium text-right',
                        mono && 'font-mono text-xs',
                        color
                    )}
                >
                    {value}
                </span>
            ) : (
                value
            )}
            {copyable && onCopy && (
                <button
                    onClick={onCopy}
                    className="opacity-0 group-hover:opacity-100 transition-opacity p-0.5 rounded hover:bg-slate-200"
                    type="button"
                    title="Copy to clipboard"
                >
                    <Copy size={12} className="text-slate-400" />
                </button>
            )}
        </div>
    </div>
);